import React from "react";

import {
  BackupIcon,
  BlackfireIcon as BlackFire,
  CertifiedIcon as Certified,
  ClearBuildCacheIcon as ClearBuildCache,
  CommitIcon,
  ConfigureIcon,
  CronIcon as Cron,
  MergeIcon,
  PausedIcon,
  PowerIcon as Power,
  RedeployIcon,
  ResumeIcon,
  SettingsIcon,
  SyncIcon,
  UpgradeIcon,
  UserIcon,
  BellIcon,
  DeployIcon
} from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { ACTIVITY_TYPES } from "Constants/constants";
import {
  Bitbucket,
  GitHub,
  Gitlab,
  Script,
  Email,
  Pagerduty,
  Slack,
  Webhook
} from "Icons/integrations";
import { getRouteUrl } from "Libs/utils";

import ActivityCancelTitle from "./Activity/ActivityCancelTitle";
import ActivityTitle from "./Activity/ActivityTitle";
import * as S from "./Activity/ActivityTitle/ActivityTitle.styles";
import { useReloadDeployment } from "./hooks/useReloadDeployment";

const cleanHtml = _props => {
  let description = _props.activity.description;
  let linkURL = "/";
  if (description.includes("</environment>")) {
    const projectURL = getProjectURL(_props);

    linkURL = `${projectURL}/${encodeURIComponent(
      _props.activity.payload["environment"].id
    )}`;
  }

  /**
   * <[^/environment].*?> Any starting tag that doesn't have <environment>
   * <\/[^environment].*?> Any ending tag that doesn't have </environment>
   * <environment.*?> Any starting tag that does have <environment>
   * <\/environment.*?> Any ending tag that does have </environment>
   */
  const toBeReplaced = [
    /<[^/environment].*?>/g,
    /<\/[^environment].*?>/g,
    /<environment.*?>/g,
    /<\/environment.*?>/g
  ];

  const replaceWith = [
    "<strong>",
    "</strong>",
    `<a style="font-weight: bold;
    text-decoration: none;
    color: inherit;" href="${linkURL}">`,
    `</a>`
  ];

  toBeReplaced.forEach(
    (value, i) => (description = description.replace(value, replaceWith[i]))
  );
  return description;
};

const GetMoreLink = ({ linkId }: { linkId?: string }) => {
  const intl = useIntl();
  if (!linkId || !(intl.messages[linkId]?.length > 0)) return null;

  return (
    <S.LearnMoreLink href={intl.formatMessage({ id: linkId })}>
      {intl.formatMessage({ id: "learnmore" })}
    </S.LearnMoreLink>
  );
};

const getProjectURL = _props =>
  `/${_props.organizationId}/${_props.activity.project}`;

// eslint-disable-next-line react/display-name
const getLinkTo = k => _props => {
  const projectURL = getProjectURL(_props);
  const linkURL = `${projectURL}/${encodeURIComponent(
    _props.activity.payload[k].id
  )}`;
  const linkTitle = _props.activity.payload[k].title;
  return <Link to={linkURL}>{linkTitle}</Link>;
};

const getLinkToNewProject = _props => {
  const linkURL = getProjectURL(_props);
  const linkTitle = _props.activity.payload.outcome.title;
  return <Link to={linkURL}>{linkTitle}</Link>;
};

const getLinkToParentEnv = getLinkTo("parent");
const getLinkToEnvironment = getLinkTo("environment");
const getLinkToOutcome = getLinkTo("outcome");

const getAccessDisplayName = _props =>
  _props.activity.payload?.access?.display_name;

const getOutcomeTitle = _props =>
  _props.activity.payload.outcome
    ? _props.activity.payload.outcome.title
    : undefined;

const getRouteTitle = _props =>
  getRouteUrl(_props.activity.payload.route.route);

const getResumeProjectTitle = _props => _props.projectTitle;
const getVariableName = _props => _props.activity.payload.variable.name;
const getDomainName = _props => _props.activity.payload.domain.name;
const getNewTitle = _props => _props.activity.payload.new_title;
const getClearBuildCacheName = () => "build cache";
const getClearBuildCacheProject = _props => {
  return _props.activity.payload?.project?.title ?? "project";
};
const getUpgradeTitle = _props =>
  _props.activity.text.replace("Upgrading the", "");
const getUpgradeVersion = _props => _props.activity.parameters.new_version;
const getUpgradeSecondaryText = _props =>
  _props.activity.state === "scheduled"
    ? "upgrade_to_version"
    : "upgraded_to_version";

const descriptionTitle = _props => {
  const description = cleanHtml(_props);

  return <ActivityTitle name="" description={description} />;
};

const descriptionCancelTitle = _props => {
  const description = cleanHtml(_props);

  return (
    <S.CancelTitleWrapper dangerouslySetInnerHTML={{ __html: description }} />
  );
};

const AutoscalingAlertTitle = ({ activity }) => {
  const intl = useIntl();
  const alertDetails = activity.payload.alert;
  const envResourceState =
    activity?.payload?.environment_resources_update_activity?.state;
  const activityType = activity.type;
  const isAlert = activityType === "environment.alert";

  const scaling = intl.formatMessage(
    { id: "activity.scaling" },
    {
      strong: (content: React.ReactNode) => (
        <S.InlineText weight="bold">{content}</S.InlineText>
      )
    }
  );
  const noScaling = intl.formatMessage(
    { id: "activity.no.scaling" },
    {
      strong: (content: React.ReactNode) => (
        <S.InlineText weight="bold">{content}</S.InlineText>
      )
    }
  );

  const prefix = isAlert
    ? envResourceState === "skipped"
      ? noScaling
      : scaling
    : "";

  const duration =
    alertDetails.duration / 60 > 1
      ? `${alertDetails.duration / 60} minutes`
      : `${alertDetails.duration / 60} minute`;
  const resource = alertDetails.resource?.toUpperCase();
  const threshold = alertDetails.threshold;
  const condition = alertDetails.condition === "<=" ? "below" : "over";
  const service = alertDetails.service;

  const description = (
    <>
      {prefix}
      {resource} for <strong>{service}</strong> {condition}&nbsp;
      <strong>{threshold}%</strong> for {duration}
    </>
  );
  return <S.TrimTitle>{description}</S.TrimTitle>;
};

const composedTitle =
  ({
    primaryText,
    secondaryText,
    getPrimaryTitle,
    getSecondaryTitle,
    disableName = false,
    disablePrimaryText = false,
    link,
    getNote
  }: {
    primaryText?: ((args: unknown) => string) | string;
    secondaryText?: ((args: unknown) => string) | string;
    getPrimaryTitle?: (args: unknown) => React.ReactNode;
    getSecondaryTitle?: (args: unknown) => React.ReactNode;
    disableName?: boolean;
    disablePrimaryText?: boolean;
    link?: React.ReactNode;
    getNote?: (args: unknown) => string;
  }) =>
  // eslint-disable-next-line react/display-name
  _props => {
    const { display_name: name } = _props.activity.payload.user;
    const primaryTitle = getPrimaryTitle ? getPrimaryTitle(_props) : null;
    const secondaryTitle = getSecondaryTitle ? getSecondaryTitle(_props) : null;
    const noteText = getNote ? getNote(_props) : "";
    const displayPrimaryText =
      typeof primaryText === "function" ? primaryText(_props) : primaryText;
    const displaySecondaryText =
      typeof secondaryText === "function"
        ? secondaryText(_props)
        : secondaryText;

    return (
      <ActivityTitle
        name={name}
        primaryTitle={primaryTitle}
        primaryText={displayPrimaryText}
        secondaryText={displaySecondaryText}
        secondaryTitle={secondaryTitle}
        disableName={disableName}
        disablePrimaryText={disablePrimaryText}
        link={link}
        note={noteText}
      />
    );
  };

const composeCancelTitle =
  ({
    primaryText,
    secondaryText,
    getPrimaryTitle,
    getSecondaryTitle,
    descriptionTitle
  }: {
    primaryText?: ((args: unknown) => string) | string;
    secondaryText?: string;
    getPrimaryTitle?: (args: unknown) => React.ReactNode;
    getSecondaryTitle?: (args: unknown) => React.ReactNode;
    descriptionTitle?: (args: unknown) => React.ReactNode;
  }) =>
  // eslint-disable-next-line react/display-name
  _props => {
    const { display_name: name } = _props.activity.payload.user;

    const primaryTitle = getPrimaryTitle ? getPrimaryTitle(_props) : null;
    const secondaryTitle = getSecondaryTitle ? getSecondaryTitle(_props) : null;

    const displayPrimaryText =
      typeof primaryText === "function" ? primaryText(_props) : primaryText;

    return (
      <ActivityCancelTitle
        name={name}
        primaryTitle={primaryTitle as any}
        primaryText={displayPrimaryText}
        secondaryText={secondaryText}
        secondaryTitle={secondaryTitle as any}
        description={descriptionTitle as any}
      />
    );
  };

const getBackupPrimaryText = _props => {
  return _props.activity.state !== "complete"
    ? "creating_backup"
    : _props.activity.result !== "failure"
      ? "created_backup"
      : "attempt_create_backup";
};

const getSyncPrimaryText = _props => {
  const { state, result } = _props.activity;

  if (state === "complete" && result !== "failure") {
    return "synced";
  } else if (state !== "complete" || result === "failure") {
    return "initiated_sync";
  }

  return "";
};

const getSyncPrimaryTitle = props => {
  const projectURL = getProjectURL(props);
  const { outcome, synchronization_type } = props.activity.payload;
  const linkURL = `${projectURL}/${outcome?.id}`;

  return (
    <span>
      {synchronization_type}
      <S.PlainText>
        {" "}
        <FormattedMessage id="from" />{" "}
      </S.PlainText>
      <strong>{outcome?.parent}</strong>
      <S.PlainText>
        {" "}
        <FormattedMessage id="to" />{" "}
      </S.PlainText>
      <Link to={linkURL}>
        <strong>{outcome?.title}</strong>
      </Link>
    </span>
  );
};

const ScaleTitle = _props => {
  const activity = _props.activity;
  const intl = useIntl();

  const deploymentType = activity.payload.deployment_type;
  const isAutoscalingActivity =
    activity.type === "environment.resources.update";

  const service = activity?.payload?.resources_updated?.webapps
    ? Object.keys(activity.payload.resources_updated.webapps)[0]
    : "your app";
  const upscaleTitle = intl.formatMessage(
    { id: "activity.upscale.title" },
    {
      strong: (content: React.ReactNode) => (
        <S.InlineText weight="bold">{content}</S.InlineText>
      ),
      service: <S.InlineText weight="bold">{service}</S.InlineText>
    }
  );

  const downscaleTitle = intl.formatMessage(
    { id: "activity.downscale.title" },
    {
      strong: (content: React.ReactNode) => (
        <S.InlineText weight="bold">{content}</S.InlineText>
      ),
      service: <S.InlineText weight="bold">{service}</S.InlineText>
    }
  );
  if (isAutoscalingActivity) {
    if (deploymentType === undefined) {
      return composedTitle({
        primaryText: "resources_updated_text",
        getPrimaryTitle: () => (
          <FormattedMessage id="resources_allocation_text" />
        ),
        secondaryText: "resources_on_text",
        getSecondaryTitle: getLinkToEnvironment
      })(_props);
    }

    return deploymentType === "autoscale.horizontal.down" ? (
      <S.TrimTitle>{downscaleTitle}</S.TrimTitle>
    ) : (
      <S.TrimTitle>{upscaleTitle}</S.TrimTitle>
    );
  }

  return "";
};

const getBackupName = _props => _props.activity.payload.backup_name || "";
const getEnvironmentId = _props => _props.activity.payload.environment.id || "";
const getProfile = _props => _props.activity.payload.profile || "";
const getOperation = _props => _props.activity.payload.operation;

const activityTemplates = {
  [ACTIVITY_TYPES.ENVIRONMENT.ACCESS.ADD]: {
    icon: <UserIcon />,
    title: composedTitle({
      primaryText: "added",
      secondaryText: "to",
      getPrimaryTitle: getAccessDisplayName,
      getSecondaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "added",
      secondaryText: "to",
      getPrimaryTitle: getAccessDisplayName,
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.ACCESS.REMOVE]: {
    icon: <UserIcon />,
    title: composedTitle({
      primaryText: "removed",
      secondaryText: "from",
      getPrimaryTitle: getAccessDisplayName,
      getSecondaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "removed",
      secondaryText: "from",
      getPrimaryTitle: getAccessDisplayName,
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.BACKUP]: {
    icon: <BackupIcon />,
    title: composedTitle({
      primaryText: getBackupPrimaryText,
      secondaryText: "of",
      getPrimaryTitle: getBackupName,
      getSecondaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: getBackupPrimaryText,
      secondaryText: "of",
      getPrimaryTitle: getBackupName,
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.DOMAIN.UPDATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated domain",
      getPrimaryTitle: getDomainName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated domain",
      getPrimaryTitle: getDomainName
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.BACKUP_DELETE]: {
    icon: <BackupIcon />,
    title: composedTitle({
      primaryText: "deleted_backup",
      secondaryText: "of",
      getPrimaryTitle: getBackupName,
      getSecondaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deleted_backup",
      secondaryText: "of",
      getPrimaryTitle: getBackupName,
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.BRANCH]: {
    icon: <S.BranchIcon />,
    title: composedTitle({
      primaryText: "branched_environment",
      secondaryText: "from",
      getPrimaryTitle: getLinkToOutcome,
      getSecondaryTitle: getLinkToParentEnv
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "branched_environment",
      secondaryText: "from",
      getPrimaryTitle: getLinkToOutcome,
      getSecondaryTitle: getLinkToParentEnv
    })
  },

  [ACTIVITY_TYPES.ENVIRONMENT.CRON]: {
    icon: <Cron />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.ENVIRONMENT.DEACTIVATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "deactivated_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deactivated_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.ACTIVATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "activated_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "activated_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.ALERT]: {
    icon: <BellIcon color="var(--mode-text-neutral-weakest)" />,
    title: AutoscalingAlertTitle
  },
  [ACTIVITY_TYPES.ENVIRONMENT.DELETE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "deleted_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deleted_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "initialized_environment",
      getPrimaryTitle: getOutcomeTitle,
      secondaryText: "with_profile",
      getSecondaryTitle: getProfile
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "initialized_environment",
      getPrimaryTitle: getOutcomeTitle,
      secondaryText: "with_profile",
      getSecondaryTitle: getProfile
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.MERGE]: {
    icon: <MergeIcon />,
    title: composedTitle({
      primaryText: "merged",
      getPrimaryTitle: getLinkToOutcome,
      secondaryText: "into",
      getSecondaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "merged",
      getPrimaryTitle: getLinkToOutcome,
      secondaryText: "into",
      getSecondaryTitle: getLinkToEnvironment
    }),
    notification: useReloadDeployment
  },
  [ACTIVITY_TYPES.ENVIRONMENT.PUSH]: {
    icon: <CommitIcon />,
    title: composedTitle({
      primaryText: "pushed_to",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "pushed_to",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.DEPLOY]: {
    icon: <DeployIcon />,
    title: composedTitle({
      primaryText: "deployed_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deployed_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.REDEPLOY]: {
    icon: <RedeployIcon />,
    title: composedTitle({
      primaryText: "redeployed_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "redeployed_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.RESTORE]: {
    icon: <BackupIcon />,
    title: composedTitle({
      primaryText: "restored_backup",
      getPrimaryTitle: getBackupName,
      secondaryText: "restored_to_environment",
      getSecondaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "restored_backup",
      getPrimaryTitle: getBackupName,
      secondaryText: "restored_to_environment",
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.PAUSE]: {
    icon: <PausedIcon />,
    title: composedTitle({
      primaryText: "paused_text",
      getPrimaryTitle: getLinkToEnvironment,
      secondaryText: "environment_text"
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.RESUME]: {
    icon: <ResumeIcon />,
    title: composedTitle({
      primaryText: "resumed_text",
      getPrimaryTitle: getLinkToEnvironment,
      secondaryText: "environment_text"
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE]: {
    icon: <ConfigureIcon />,
    title: ScaleTitle,
    cancelTitle: composeCancelTitle({
      primaryText: "resources_updated_text",
      getPrimaryTitle: () => (
        <FormattedMessage id="resources_allocation_text" />
      ),
      secondaryText: "resources_on_text",
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.ROUTE.CREATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "added_route",
      getPrimaryTitle: getRouteTitle
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "added_route",
      getPrimaryTitle: getRouteTitle
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.ROUTE.DELETE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "deleted_route",
      getPrimaryTitle: getRouteTitle
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deleted_route",
      getPrimaryTitle: getRouteTitle
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.ROUTE.UPDATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "modified_route",
      getPrimaryTitle: getRouteTitle
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "modified_route",
      getPrimaryTitle: getRouteTitle
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "source_operation_executed",
      secondaryText: "source_operation_on_env",
      getPrimaryTitle: getOperation,
      getSecondaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.SUBSCRIPTION.UPDATE]: {
    icon: <SettingsIcon />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.ENVIRONMENT.UPDATE.HTTP_ACCESS]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated_HTTP_Access_settings_on_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated_HTTP_Access_settings_on_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.UPDATE.RESTRICT_ROBOTS]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated_the_robots.txt_settings_on_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated_the_robots.txt_settings_on_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.UPDATE.SMTP]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated_SMTP_settings_on_environment",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated_SMTP_settings_on_environment",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.VARIABLE.CREATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "added_environment_variable",
      getPrimaryTitle: getVariableName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "added_environment_variable",
      getPrimaryTitle: getVariableName
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.VARIABLE.DELETE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "deleted_environment_variable",
      getPrimaryTitle: getVariableName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deleted_environment_variable",
      getPrimaryTitle: getVariableName
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.VARIABLE.UPDATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated_environment_variable",
      getPrimaryTitle: getVariableName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated_environment_variable",
      getPrimaryTitle: getVariableName
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.SYNCHRONIZE]: {
    icon: <SyncIcon />,
    title: composedTitle({
      primaryText: getSyncPrimaryText,
      getPrimaryTitle: getSyncPrimaryTitle,
      getSecondaryTitle: getEnvironmentId
    }),
    cancelTitle: composeCancelTitle({
      primaryText: getSyncPrimaryText,
      getPrimaryTitle: getSyncPrimaryTitle,
      getSecondaryTitle: getEnvironmentId
    })
  },
  [ACTIVITY_TYPES.MAINTENANCE.UPGRADE]: {
    icon: <UpgradeIcon />,
    title: composedTitle({
      disableName: true,
      disablePrimaryText: true,
      secondaryText: getUpgradeSecondaryText,
      getPrimaryTitle: getUpgradeTitle,
      getSecondaryTitle: getUpgradeVersion,
      link: <GetMoreLink linkId="links.documentation.administration.servers" />
    })
  },
  [ACTIVITY_TYPES.ENVIRONMENT.CERTIFICATE_RENEWAL]: {
    icon: <Certified />,
    title: composedTitle({
      primaryText: "renewal_environment_certificate",
      getPrimaryTitle: getLinkToEnvironment
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "renewal_environment_certificate",
      getPrimaryTitle: getLinkToEnvironment
    })
  },
  [ACTIVITY_TYPES.INTEGRATION.BITBUCKET.FETCH]: {
    icon: <Bitbucket />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.BITBUCKET.REGISTER_HOOKS]: {
    icon: <Bitbucket />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.BITBUCKET.SERVER.FETCH]: {
    icon: <Bitbucket />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.BITBUCKET.SERVER.REGISTER_HOOKS]: {
    icon: <Bitbucket />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.GITHUB.FETCH]: {
    icon: <GitHub />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.GITLAB.FETCH]: {
    icon: <Gitlab />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.HEALTH.EMAIL]: {
    icon: <Email />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.HEALTH.PAGERDUTY]: {
    icon: <Pagerduty />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.HEALTH.SLACK]: {
    icon: <Slack />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.SCRIPT]: {
    icon: <Script />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.WEBHOOK]: {
    icon: <Webhook />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.HEALTH.WEBHOOK]: {
    icon: <Webhook />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  },
  [ACTIVITY_TYPES.INTEGRATION.BLACKFIRE.WEBHOOK]: {
    icon: <BlackFire />,
    title: descriptionTitle
  },
  [ACTIVITY_TYPES.PROJECT.SUSPEND]: {
    icon: <PausedIcon />,
    title: composedTitle({
      primaryText: "activity.suspend.suspended",
      getPrimaryTitle: getResumeProjectTitle
    })
  },
  [ACTIVITY_TYPES.PROJECT.RESUME]: {
    icon: <Power />,
    title: composedTitle({
      primaryText: "unsuspended",
      getPrimaryTitle: getResumeProjectTitle
    })
  },
  [ACTIVITY_TYPES.PROJECT.CLEAR_BUILD_CACHE]: {
    icon: <ClearBuildCache />,
    title: composedTitle({
      primaryText: "cleared_the",
      secondaryText: "for",
      getPrimaryTitle: getClearBuildCacheName,
      getSecondaryTitle: getClearBuildCacheProject
    })
  },
  [ACTIVITY_TYPES.PROJECT.CREATE]: {
    icon: <CommitIcon />,
    title: composedTitle({
      primaryText: "created_a_new_project",
      getPrimaryTitle: getLinkToNewProject
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "created_a_new_project",
      getPrimaryTitle: getLinkToNewProject
    })
  },
  [ACTIVITY_TYPES.PROJECT.DOMAIN.CREATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "added_domain",
      getPrimaryTitle: getDomainName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "added_domain",
      getPrimaryTitle: getDomainName
    })
  },
  [ACTIVITY_TYPES.PROJECT.DOMAIN.DELETE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "deleted_domain",
      getPrimaryTitle: getDomainName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deleted_domain",
      getPrimaryTitle: getDomainName
    })
  },
  [ACTIVITY_TYPES.PROJECT.DOMAIN.UPDATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated",
      getPrimaryTitle: getDomainName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated",
      getPrimaryTitle: getDomainName
    })
  },
  [ACTIVITY_TYPES.PROJECT.MODIFY_TITLE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "changed_project_name_to",
      getPrimaryTitle: getNewTitle
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "changed_project_name_to",
      getPrimaryTitle: getNewTitle
    })
  },
  [ACTIVITY_TYPES.PROJECT.VARIABLE.CREATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "added_project_variable",
      getPrimaryTitle: getVariableName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "added_project_variable",
      getPrimaryTitle: getVariableName
    })
  },
  [ACTIVITY_TYPES.PROJECT.VARIABLE.DELETE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "deleted_project_variable",
      getPrimaryTitle: getVariableName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "deleted_project_variable",
      getPrimaryTitle: getVariableName
    })
  },
  [ACTIVITY_TYPES.PROJECT.VARIABLE.UPDATE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "updated_project_variable",
      getPrimaryTitle: getVariableName
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "updated_project_variable",
      getPrimaryTitle: getVariableName
    })
  },
  [ACTIVITY_TYPES.CDN_FASTLY_ORIGIN.RESTRICTION.SETUP]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "setup_fastly_origin_restriction",
      getPrimaryTitle: () => false
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "setup_fastly_origin_restriction",
      getPrimaryTitle: () => false
    })
  },
  [ACTIVITY_TYPES.CDN_FASTLY_ORIGIN.RESTRICTION.DISABLE]: {
    icon: <SettingsIcon />,
    title: composedTitle({
      primaryText: "disable_fastly_origin_restriction",
      getPrimaryTitle: () => false
    }),
    cancelTitle: composeCancelTitle({
      primaryText: "disable_fastly_origin_restriction",
      getPrimaryTitle: () => false
    })
  },
  unknown: {
    icon: <SettingsIcon />,
    title: descriptionTitle,
    cancelTitle: descriptionCancelTitle
  }
};

export default activityTemplates;
